import React, { useEffect } from 'react';
import { applyPageMeta } from '@/lib/seo';

export default function TermsAndConditions() {

  useEffect(() => {
    applyPageMeta({
      title: 'Terms and Conditions — Statbox',
      description:
        'Terms and conditions for using the Statbox website, app, and sports analytics services.',
      path: '/terms',
    });
  }, []);

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="px-6 py-8 sm:px-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms and Conditions</h1>
            <p className="text-gray-500 mb-1">Terms and Conditions for Statbox</p>
            <p className="text-gray-500 mb-8">Last Updated: August 11, 2025</p>

            <div className="space-y-10 text-gray-700 leading-7">
              <section>
                <p><strong>Welcome to Statbox!</strong></p>
                <p>
                  Statbox is a digital platform owned and operated by Hjeza Incorporated SAL(“Statbox”, “Platform”, “we”, “us” or “our”).
                  Statbox enables individuals to access matches footage and obtain sport statistics and highlights (“User” or “You”). Matches are recorded at selected sports facilities ( each a “Venue”).
                  By accessing or using our website at statbox.co (“Site”), application, and services (collectively, the “Services”), you agree to comply with and be bound by these terms and conditions (“Terms”).
                </p>
                <p>You are also agreeing to our Privacy Policy and Cookie Policy and agreeing to follow any other rules on the Site.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">1. CAPACITY REQUIREMENT</h2>
                <p>By using our Services, you confirm that you are at least 18 years old or have the legal capacity to agree to these Terms.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">2. SERVICES PROVIDED</h2>
                <p>Our Services include, but are not limited to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Tracking and recording sports game statistics.</li>
                  <li>Providing analysis tools to assess player and team performance.</li>
                  <li>User account features for customization and access control.</li>
                  <li>Communication features for sharing and managing game data.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">3. ACCOUNT REGISTRATION</h2>
                <p>To access certain features of the Services, you must create an account. When creating an account, you agree to:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Account registration:</strong> Provide accurate and complete information. We reserve the right to request any documentation to verify your identity, in accordance with the requirements of the applicable laws and regulations.</li>
                  <li><strong>User responsibilities:</strong> Keep your account credentials secure and confidential. Notify us immediately if you suspect unauthorized access to your account.</li>
                  <li><strong>Account deletion:</strong> You may delete your account at any moment, however, please be aware that doing so will not necessarily and automatically result in the deletion of the content. We reserve the right to retain certain information as needed for legitimate business purposes or compliance with applicable laws and regulations.</li>
                  <li><strong>Account termination:</strong> We reserve the right to suspend or terminate your account without prior notice and without refund if you violate the applicable laws and regulations, these Terms, or engage in any unlawful activity in addition to initiating legal actions where applicable.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">4. USE OF THE PLATFORM</h2>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Permitted use:</strong> Use the Services only for lawful purposes and in compliance with applicable laws and regulations.</li>
                  <li><strong>Prohibited use:</strong> you agree not to:
                    <ul className="list-disc pl-6 space-y-1">
                      <li>(i) download, copy, or distribute video content, highlights, or data in violation of the Platform’s access rights;</li>
                      <li>(ii) interfere with, disrupt or attempt to gain unauthorized access to the Platform’s servers, data, or underlying code;</li>
                      <li>(iii) use the Platform in any ways that violates the intellectual property or privacy rights of others, including unauthorized use of video footage or personal statistics;</li>
                      <li>(iv) use the Platform for any unlawful, fraudulent, or harmful purpose, or in any manner that could damage, disable, or impair the Platform or the  Services.</li>
                    </ul>
                  </li>
                  <li><strong>Data responsibility:</strong> Be solely responsible for any data, content, or information you upload, share, or store within the Platform.</li>
                </ul>
                <p className="mt-2">It is to be noted that in case of any prohibited use, Statbox is entitled at it discretion to to  immediatatly  suspend or terminate  your access to the Platform and its services, without refund, and reserve its right to initiate any  legal action where applicable.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">5. PAYMENT AND BILLING</h2>
                <p>Some features of Statbox may require payment. By using such features, you agree to the following:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Payments will be processed through our secure payment gateway.</li>
                  <li>All fees are non-refundable unless the features does not meet the expected standards or are not as described.</li>
                  <li>You are responsible for providing valid payment information and ensuring timely payment.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">6. DATA AND PRIVACY</h2>
                <ul className="list-disc pl-6 space-y-1">
                  <li><strong>Data collection:</strong> we collect personal data as described in our Privacy Policy.</li>
                  <li><strong>Data use:</strong> collected data is used to operate and improve the Platform, and for all the other purposes outlined in the Privacy Policy.</li>
                  <li><strong>Users rights:</strong> Users have rights regarding their data, including the right to access, correct, and delete their personal information. For more information, please refer to our Privacy Policy.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">7. INTELLECTUAL PROPERTY</h2>
                <p>All content and materials provided within the Services, including but not limited to text, graphics, logos, icons, images, audio clips, video, software, and any other digital assets are exclusively owned by or licensed to Statbox and are protected by applicable copyright, trademark and other intellectual property laws.</p>
                <p>You may not, without prior express written consent from :</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Copy, reproduce, or distribute our content without explicit written consent.</li>
                  <li>Use our trademarks or branding without permission.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">8. LIMITATION OF LIABILITY</h2>
                <p>To the maximum extent permitted by law, Statbox is not liable for:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Any indirect, incidental, or consequential damages arising from your use of our Services.</li>
                  <li>Errors, interruptions, or data loss resulting from your use of the Services.</li>
                </ul>
                <p>We provide our Services on an "as is" and "as available" basis and disclaim all warranties, express or implied.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">10. THIRD-PARTY SERVICES</h2>
                <p>Our Services may include links to or integrations with third-party services. We do not control or endorse these services and are not responsible for their content or practices. Use of third-party services is at your own risk.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">11. GOVERNING LAW</h2>
                <p>These Terms are governed by and construed in accordance with the laws of Lebanon. Any disputes arising under these Terms will be subject to the exclusive jurisdiction of the courts in Beirut, Lebanon.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">12. CHANGES TO TERMS</h2>
                <p>We may update these Terms from time to time to reflect changes in our Services or applicable laws. The updated version will be effective as of the "Last Updated" date. Your continued use of the Services constitutes acceptance of the updated Terms.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-3">13. CONTACT US</h2>
                <p>If you have any questions or concerns about these Terms, please contact us at:</p>
                <p>Email: <a href="mailto:support@statbox.co" className="text-blue-600 hover:text-blue-800 underline">support@statbox.co</a></p>
              </section>

              <p className="text-gray-700">By using Statbox, you acknowledge that you have read, understood, and agree to these Terms and Conditions.</p>
            </div>

            <div className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-gray-600">
                If you have questions about these Terms, please contact us at{' '}
                <a href="mailto:support@statbox.co" className="text-blue-600 hover:text-blue-800 underline">
                  support@statbox.co
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
